import { EventEmitter, inject, Injectable, NgZone } from '@angular/core';
import { Task } from '@shared/models/task.model';
import { TaskGroup } from '@shared/models/task-group.model';
import { ListGroupTask } from '@shared/modules/list/model/list-group-task.model';
import { TranslateService } from '@ngx-translate/core';
import { TaskStatus } from '@shared/enums/task-status.enum';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { ETaskTypeGroup } from '@shared/modules/task-sidenav/enums/task-group.enum';
import { Project } from '@modules/projects/shared/models/project.model';
import { ProjectTaskAPIService } from '@modules/projects/shared/services/project-task-api.service';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { SnackBarService } from '@core/services/snackbar.service';
import { Subject, throwError } from 'rxjs';
import { TaskService } from '@shared/modules/tasks/services/task-service';

@Injectable({
  providedIn: 'root'
})
export class ListTaskService {
  project: Project;
  projectId: number;
  isMyWorkTaskLists: boolean = false;
  emitter: EventEmitter<ListTaskEvent> = new EventEmitter<ListTaskEvent>();
  public attachmentSub = new Subject();
  public attachmentSub$ = this.attachmentSub.asObservable();

  static LIST_GROUP_TASK_PREFIX = 'lgt_';
  static TASK_PREFIX = 't_';

  private _listGroupTasksHashMap: Map<string, ListGroupTask> = new Map<string, ListGroupTask>();
  private _tasksHashMap: Map<string, Task> = new Map<string, Task>();
  private _completedTasksHashMap: Map<string, Task> = new Map<string, Task>();
  private _cancelledTasksHashMap: Map<string, Task> = new Map<string, Task>();
  public originalData: ListGroupTask[];

  private projectApiTaskService = inject(ProjectTaskAPIService);
  private snackbar = inject(SnackBarService);
  private zone = inject(NgZone);

  get tasksHashMap() {
    return this._tasksHashMap;
  }

  get completedTasksHashMap() {
    return this._completedTasksHashMap;
  }

  get cancelledTasksHashMap() {
    return this._cancelledTasksHashMap;
  }

  get listGroupTasksHashMap(): Map<string, any> {
    return this._listGroupTasksHashMap;
  }

  set listGroupsTask(listGroupsTask: ListGroupTask[]) {
    // If it is workspace view and there is initial structure from API we need to prepare the data to display
    if (this.isMyWorkTaskLists && listGroupsTask.some((task) => !!task.data?.length)) {
      listGroupsTask = this.prepareGroupTasksForMyWork(listGroupsTask);
    }

    this.setHashMaps(listGroupsTask);
  }

  constructor(
    private t: TranslateService,
    private taskService: TaskService
  ) {}

  getTask(taskId: number | string): Task | null {
    return new Map([...this.tasksHashMap, ...this.completedTasksHashMap, ...this.cancelledTasksHashMap]).get(
      `${ListTaskService.TASK_PREFIX}${taskId}`
    );
  }

  updateTask(task: Task): void {
    if (this.tasksHashMap.get(this.getKeyForTask(task))) {
      this.tasksHashMap.set(this.getKeyForTask(task), new Task(task));
    }
    if (this.completedTasksHashMap.get(this.getKeyForTask(task))) {
      this.completedTasksHashMap.set(this.getKeyForTask(task), new Task(task));
    }
    if (this.cancelledTasksHashMap.get(this.getKeyForTask(task))) {
      this.cancelledTasksHashMap.set(this.getKeyForTask(task), new Task(task));
    }

    this.updateTaskInHashGroup(task);
  }

  addTask(task: Task): void {
    if (task.status === TaskStatus.COMPLETED) {
      this.completedTasksHashMap.set(this.getKeyForTask(task), new Task(task));
    } else if (task.status === TaskStatus.CANCELLED) {
      this.cancelledTasksHashMap.set(this.getKeyForTask(task), new Task(task));
    } else {
      this.tasksHashMap.set(this.getKeyForTask(task), new Task(task));
    }

    this.addTaskToHashGroup(task);
  }

  updateTaskInHashGroup(task: Task) {
    const groupId = this.isMyWorkTaskLists ? task.workspaceTaskGroup?.id : task.taskGroup?.id;

    if (!groupId) return;

    const key = `${ListTaskService.LIST_GROUP_TASK_PREFIX}${groupId}`;
    const hashGroup = this.listGroupTasksHashMap.get(key);

    if (!hashGroup) return;

    const findIndex = hashGroup.tasks.findIndex((t) => t.id === task.id);
    if (findIndex > -1) {
      hashGroup.tasks[findIndex] = task;
    }

    this.listGroupTasksHashMap.set(key, hashGroup);
  }

  addTaskToHashGroup(task: Task) {
    const groupId = this.isMyWorkTaskLists ? task.workspaceTaskGroup?.id : task.taskGroup?.id;
    const hashGroup = this.listGroupTasksHashMap.get(`${ListTaskService.LIST_GROUP_TASK_PREFIX}${groupId}`);
    hashGroup?.tasks.push(task);
    this.listGroupTasksHashMap.set(`${ListTaskService.LIST_GROUP_TASK_PREFIX}${groupId}`, hashGroup);
  }

  removeTask(task: Task) {
    this.tasksHashMap.delete(this.getKeyForTask(task));
    this.completedTasksHashMap.delete(this.getKeyForTask(task));
    this.cancelledTasksHashMap.delete(this.getKeyForTask(task));
    !this.isMyWorkTaskLists ? this.removeTaskFromHashGroup(task) : null;
  }

  removeTaskFromHashGroup(task: Task) {
    const groupId = this.isMyWorkTaskLists ? task.workspaceTaskGroup.id : task.taskGroup.id;
    const hashGroup = this.listGroupTasksHashMap.get(`${ListTaskService.LIST_GROUP_TASK_PREFIX}${groupId}`);
    const findIndex = hashGroup.tasks.findIndex((i) => i.id === task.id);
    if (findIndex > -1) {
      hashGroup.tasks = hashGroup.tasks.filter((i) => i.id !== task.id);
    }
    this.listGroupTasksHashMap.set(`${ListTaskService.LIST_GROUP_TASK_PREFIX}${groupId}`, hashGroup);
  }

  getKeyForTask(task: Task) {
    return `${ListTaskService.TASK_PREFIX}${task?.id}`;
  }

  prepareGroupTasksForMyWork(apiResponseData: any[]): ListGroupTask[] {
    const processedGroups: ListGroupTask[] = [];

    apiResponseData.forEach((mainApiCategory) => {
      const withProjectFlag = mainApiCategory.withProject;

      if (Array.isArray(mainApiCategory.data)) {
        mainApiCategory.data.forEach((nestedGroupData: any) => {
          const nestedGroup = {
            taskGroup: new TaskGroup(nestedGroupData.workspaceTaskGroup),
            tasks: nestedGroupData.tasks || [],
            completedTasks: nestedGroupData.completedTasks || [],
            cancelledTasks: nestedGroupData.cancelledTasks || [],
            withProject: withProjectFlag,
            isMyWorkGroup: true
          };

          processedGroups.push(nestedGroup as ListGroupTask);
        });
      }
    });

    return processedGroups.sort((a, b) => a.taskGroup.sortOrder - b.taskGroup.sortOrder);
  }

  private setHashMaps(listGroupTasks: any) {
    listGroupTasks = listGroupTasks.filter((i) => i.taskGroup.name !== ETaskTypeGroup.GROUP_CHANGE);

    this.originalData = listGroupTasks;

    const existsListGroupTasksKeys = [];
    const existsTasksKeys = [];
    const existsCompletedTasksKeys = [];
    const existsCancelledTasksKeys = [];

    for (let i = 0; i < listGroupTasks.length; i++) {
      const listGroupTask = listGroupTasks[i];
      existsListGroupTasksKeys.push(`${ListTaskService.LIST_GROUP_TASK_PREFIX}${listGroupTask.taskGroup.id}`);

      if (Array.isArray(listGroupTask.data)) {
        listGroupTask.tasks = listGroupTask.data.flatMap((d) => d.tasks || []);
        listGroupTask.completedTasks = listGroupTask.data.flatMap((d) => d.completedTasks || []);
        listGroupTask.cancelledTasks = listGroupTask.data.flatMap((d) => d.cancelledTasks || []);
      } else {
        listGroupTask.tasks = listGroupTask.tasks || [];
        listGroupTask.completedTasks = listGroupTask.completedTasks || [];
        listGroupTask.cancelledTasks = listGroupTask.cancelledTasks || [];
      }

      listGroupTask.currentAllCount = listGroupTask.tasks.length + listGroupTask.completedTasks.length;
      listGroupTask.currentCompletedCount = listGroupTask.completedTasks.length;

      this.listGroupTasksHashMap.set(
        `${ListTaskService.LIST_GROUP_TASK_PREFIX}${listGroupTask.taskGroup.id}`,
        listGroupTask
      );

      for (
        let j = 0;
        j < listGroupTask.tasks.length ||
        j < listGroupTask.completedTasks.length ||
        j < listGroupTask.cancelledTasks.length;
        j++
      ) {
        const task = listGroupTask.tasks[j];
        const completedTask = listGroupTask.completedTasks[j];
        const cancelledTask = listGroupTask.cancelledTasks[j];

        if (isNotNullOrUndefined(task)) {
          existsTasksKeys.push(this.getKeyForTask(task));
          this.tasksHashMap.set(this.getKeyForTask(task), new Task(task));
        }

        if (isNotNullOrUndefined(completedTask)) {
          existsCompletedTasksKeys.push(this.getKeyForTask(completedTask));
          this.completedTasksHashMap.set(this.getKeyForTask(completedTask), new Task(completedTask));
        }

        if (isNotNullOrUndefined(cancelledTask)) {
          existsCancelledTasksKeys.push(this.getKeyForTask(cancelledTask));
          this.cancelledTasksHashMap.set(this.getKeyForTask(cancelledTask), new Task(cancelledTask));
        }

        this.setSubtasksHashmap(
          task,
          completedTask,
          cancelledTask,
          existsTasksKeys,
          existsCompletedTasksKeys,
          existsCancelledTasksKeys
        );
      }
    }
    this.removeNotExistsTasksFromHashmap(
      existsListGroupTasksKeys,
      existsTasksKeys,
      existsCompletedTasksKeys,
      existsCancelledTasksKeys
    );
    this.sortTaskInHashmap();
  }

  private setSubtasksHashmap(
    task: Task,
    completedTask: Task,
    cancelledTask: Task,
    existsTasksKeys: string[],
    existsCompletedTasksKeys: string[],
    existsCancelledTasksKeys: string[]
  ) {
    for (
      let k = 0;
      k < task?.children.length || k < completedTask?.children.length || k < cancelledTask?.children.length;
      k++
    ) {
      const subtask = task ? task.children[k] : null;
      const completedSubtask = completedTask ? completedTask.children[k] : null;
      const cancelledSubtask = cancelledTask ? cancelledTask.children[k] : null;
      if (isNotNullOrUndefined(subtask)) {
        existsTasksKeys.push(this.getKeyForTask(subtask));
        this.tasksHashMap.set(this.getKeyForTask(subtask), new Task(subtask));
      }

      if (isNotNullOrUndefined(completedSubtask)) {
        existsCompletedTasksKeys.push(this.getKeyForTask(completedSubtask));
        this.completedTasksHashMap.set(this.getKeyForTask(completedSubtask), new Task(completedSubtask));
      }

      if (isNotNullOrUndefined(cancelledSubtask)) {
        existsCancelledTasksKeys.push(this.getKeyForTask(cancelledSubtask));
        this.cancelledTasksHashMap.set(this.getKeyForTask(cancelledSubtask), new Task(cancelledSubtask));
      }

      this.setSubtasksHashmap(
        subtask,
        completedSubtask,
        cancelledSubtask,
        existsTasksKeys,
        existsCompletedTasksKeys,
        existsCancelledTasksKeys
      );
    }
  }

  sortTaskInHashmap() {
    let structuralData: { [key: string]: { tasks: Task[]; completedTasks: Task[]; cancelledTasks: Task[] } } =
      {};

    const foreach = (map: Map<string, Task>) => {
      map.forEach((task: Task, key: string) => {
        let groupId = this.isMyWorkTaskLists ? task.workspaceTaskGroup?.id : task?.taskGroup?.id;
        if (!isNotNullOrUndefined(structuralData[groupId])) {
          structuralData[groupId] = {
            tasks: [],
            completedTasks: [],
            cancelledTasks: []
          };
        }
        if (task.status === TaskStatus.COMPLETED) {
          structuralData[groupId].completedTasks.push(task);
        } else if (task.status === TaskStatus.CANCELLED) {
          structuralData[groupId].cancelledTasks.push(task);
        } else {
          structuralData[groupId].tasks.push(task);
        }
      });
    };

    foreach(this.tasksHashMap);
    foreach(this.completedTasksHashMap);
    foreach(this.cancelledTasksHashMap);

    const getOrder = (task: Task): number =>
      this.isMyWorkTaskLists ? task.workspaceTaskGroupOrder : (task.sortOrder ?? task.taskGroupOrder);

    Object.values(structuralData).forEach((item) => {
      const sorting = (arr: Task[]) => {
        const highPriorityTasks = arr
          .filter((t) => t.isHighPriority)
          .sort((a, b) => getOrder(a) - getOrder(b));

        const notHighPriorityTasks = arr
          .filter((t) => !t.isHighPriority)
          .sort((a, b) => getOrder(a) - getOrder(b));

        const sortedArr = [...highPriorityTasks, ...notHighPriorityTasks];

        return sortedArr;
      };

      item.tasks = sorting(item.tasks.filter((t) => !t?.parentId));
      item.completedTasks = sorting(item.completedTasks);
      item.cancelledTasks = sorting(item.cancelledTasks);
    });
  }

  private removeNotExistsTasksFromHashmap(
    existsListGroupTasksKeys: string[],
    existsTasksKeys: string[],
    existsCompletedTasksKeys: string[],
    existsCancelledTasksKeys: string[]
  ) {
    this.removeListGroupTasksFromHashMap(existsListGroupTasksKeys);
    this.removeTaskFromHashmap(existsTasksKeys);
    this.removeCompletedTaskFromHashmap(existsCompletedTasksKeys);
    this.removeCancelledTaskFromHashmap(existsCancelledTasksKeys);
  }

  private removeTaskFromHashmap(existsKeys: string[]) {
    for (const [key, _] of this.tasksHashMap) {
      if (existsKeys.indexOf(key) === -1) {
        this.tasksHashMap.delete(key);
      }
    }
  }

  private removeCompletedTaskFromHashmap(existsKeys: string[]) {
    for (const [key, _] of this.completedTasksHashMap) {
      if (existsKeys.indexOf(key) === -1) {
        this.completedTasksHashMap.delete(key);
      }
    }
  }

  private removeCancelledTaskFromHashmap(existsKeys: string[]) {
    for (const [key, _] of this.cancelledTasksHashMap) {
      if (existsKeys.indexOf(key) === -1) {
        this.cancelledTasksHashMap.delete(key);
      }
    }
  }

  private removeListGroupTasksFromHashMap(existsKeys: string[]) {
    for (const [key, _] of this.listGroupTasksHashMap) {
      if (existsKeys.indexOf(key) === -1) {
        this.listGroupTasksHashMap.delete(key);
      }
    }
  }

  clearGroups() {
    this._tasksHashMap.clear();
    this._completedTasksHashMap.clear();
    this._cancelledTasksHashMap.clear();
    this._listGroupTasksHashMap.clear();
  }

  clearService() {
    this.projectId = null;
    this.clearGroups();
  }

  moveTaskToGroup(task: Task, targetGroup: ListGroupTask, targetIndex: number): void {
    const isMyWork = this.isMyWorkTaskLists;

    const currentGroupId = isMyWork ? task.workspaceTaskGroup?.id : task.taskGroup?.id;

    const currentGroup = this.listGroupTasksHashMap.get(
      `${ListTaskService.LIST_GROUP_TASK_PREFIX}${currentGroupId}`
    );

    if (currentGroup) {
      currentGroup.tasks = currentGroup.tasks.filter((t) => t.id !== task.id);
    }

    if (isMyWork) {
      if (!task.workspaceTaskGroup) task.workspaceTaskGroup = { id: '' } as any;
      task.workspaceTaskGroup.id = targetGroup.taskGroup.id;
    } else {
      task.taskGroup.id = targetGroup.taskGroup.id;
    }

    if (!targetGroup.tasks) targetGroup.tasks = [];

    targetGroup.tasks.splice(targetIndex, 0, task);

    this.updateTaskPosition(task, targetIndex);
    this.sortTaskInHashmap();
  }

  updateTaskPosition(task: Task, targetIndex: number) {
    const groupId = this.isMyWorkTaskLists ? task.workspaceTaskGroup.id : task.taskGroup.id;

    const assignGroup$ = this.isMyWorkTaskLists
      ? this.taskService.assignTaskToGroup(task.id, groupId)
      : this.projectApiTaskService.taskGroupChange(task.id, groupId);

    const changeOrder$ = this.isMyWorkTaskLists
      ? this.projectApiTaskService.workspaceTaskOrderChange(task.id, targetIndex + 1)
      : this.projectApiTaskService.taskOrderChange(task.id, targetIndex + 1);

    assignGroup$
      .pipe(
        switchMap(() => changeOrder$),
        tap(() => {
          this.zone.run(() =>
            this.snackbar.success(this.t.instant('ProjectTasks.Task.successTaskOrderChange'))
          );
        }),
        catchError((error) => {
          this.zone.run(() => this.snackbar.error(this.t.instant('ProjectTasks.Task.errorTaskOrderChange')));
          return throwError(() => error);
        })
      )
      .subscribe();
  }
}

export class ListTaskEvent {
  type: ListTaskEventType;
  data?: Task | TaskGroup | any;
}

export enum ListTaskEventType {
  REFRESH_LIST_GROUPS,
  ADD_TASK,
  ADD_SUB_TASK,
  ADD_TASK_GROUP,
  UPDATE_TASK,
  REMOVE_TASK,
  UPDATE_COUNTERS,
  ASSIGNEE_CHANGE,
  UPDATE_TASK_ATTACHMENTS,
  UPDATE_TASK_ORDER,
  UPDATE_TASK_LIST
}
